/**
 * Groups (F1): SYSTEM category × church's own PROPER NAME × instance.
 * Each church creates its GroupType with its own name pointing to a
 * category (the category decides the available capabilities: cell →
 * meetings+attendance+devotionals; music → songs+rehearsals+setlists…).
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  getChurchesQuery,
  getGroupCategoriesQuery,
  getGroupCategoryCapabilitiesQuery,
  getGroupsQuery,
  getGroupTypesQuery,
  queryKeys,
} from "@agape/shared";
import {
  church_group,
  type ChurchGroupRecord,
  type ChurchRecord,
  group_type,
  type GroupCategoryCapabilityRecord,
  type GroupCategoryRecord,
  type GroupTypeRecord,
} from "@agape/powersync";
import { Boxes, Plus, Tags } from "@fleet/icons";

import { Link } from "@tanstack/solid-router";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { showToast } from "@/components/ui/toast";
import { Field, FormDialog } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

export function GroupsPage() {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const categories = useDomainQuery<GroupCategoryRecord>(
    () => [...queryKeys.domain.catalogs(), "categories"],
    (db) => getGroupCategoriesQuery(db),
  );
  const categoryCaps = useDomainQuery<GroupCategoryCapabilityRecord>(
    () => [...queryKeys.domain.catalogs(), "categoryCaps"],
    (db) => getGroupCategoryCapabilitiesQuery(db),
  );
  const types = useDomainQuery<GroupTypeRecord>(
    (uid) => queryKeys.domain.groupTypes(uid),
    (db) => getGroupTypesQuery(db),
  );
  const groups = useDomainQuery<ChurchGroupRecord>(
    (uid) => queryKeys.domain.groups(uid),
    (db) => getGroupsQuery(db),
  );

  const writableChurches = createMemo(() =>
    (churches.data ?? []).filter((c) => grants.can([c.id], "groups", "write")),
  );
  const churchName = (id: string | null) =>
    (churches.data ?? []).find((c) => c.id === id)?.name ?? "";
  const categoryName = (id: string | null) =>
    (categories.data ?? []).find((c) => c.id === id)?.name ?? id ?? "";
  const capsOfCategory = (categoryId: string | null) =>
    (categoryCaps.data ?? [])
      .filter((cc) => cc.category_id === categoryId)
      .map((cc) => cc.capability_id ?? "");

  // ── Create type (own name) ────────────────────────────────────────────────
  const [typeOpen, setTypeOpen] = createSignal(false);
  const [typeName, setTypeName] = createSignal("");
  const [typeCategory, setTypeCategory] = createSignal("");
  const [typeChurch, setTypeChurch] = createSignal("");
  const createType = async () => {
    const churchId = typeChurch() || writableChurches()[0]?.id;
    if (!typeName().trim() || !typeCategory() || !churchId) {
      return;
    }
    await write().insert(group_type).values({
      id: newId(),
      church_id: churchId,
      category_id: typeCategory(),
      name: typeName().trim(),
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setTypeOpen(false);
    setTypeName("");
    showToast(t(msg`Group type created`), "success");
  };

  // ── Create group (instance) ───────────────────────────────────────────────
  const [groupOpen, setGroupOpen] = createSignal(false);
  const [groupName, setGroupName] = createSignal("");
  const [groupType, setGroupType] = createSignal("");
  const createGroup = async () => {
    const type = (types.data ?? []).find((ty) => ty.id === groupType());
    if (!groupName().trim() || !type) {
      return;
    }
    await write().insert(church_group).values({
      id: newId(),
      church_id: type.church_id,
      group_type_id: type.id,
      name: groupName().trim(),
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setGroupOpen(false);
    setGroupName("");
    showToast(t(msg`Group created`), "success");
  };

  const typeOf = (g: ChurchGroupRecord) =>
    (types.data ?? []).find((ty) => ty.id === g.group_type_id);

  return (
    <div class="space-y-6">
      <PageSubHeader title={t(msg`Groups`)} />

      {/* ── Tipos de grupo (nombre propio por iglesia) ───────────────────── */}
      <section class="space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Group types`)}
          </h3>
          <Show when={writableChurches().length > 0}>
            <Button
              size="sm"
              variant="ghost"
              data-testid="group-type-create"
              onClick={() => setTypeOpen(true)}
            >
              <Tags size={14} /> {t(msg`New type`)}
            </Button>
          </Show>
        </div>
        <p class="text-xs text-muted-foreground">
          {t(msg`Each church names its own types; the system category decides the capabilities.`)}
        </p>
        <Show
          when={(types.data ?? []).length > 0}
          fallback={
            <p class="text-sm text-muted-foreground" data-testid="group-types-empty">
              {t(msg`No group types yet.`)}
            </p>
          }
        >
          <div class="flex flex-wrap gap-2" data-testid="group-types">
            <For each={types.data ?? []}>
              {(ty) => (
                <span
                  class="rounded-full border border-border px-3 py-1 text-sm"
                  data-testid="group-type-item"
                >
                  <span class="font-medium">{ty.name}</span>{" "}
                  <span class="text-muted-foreground">
                    · {categoryName(ty.category_id)} · {churchName(ty.church_id)}
                  </span>
                </span>
              )}
            </For>
          </div>
        </Show>
      </section>

      {/* ── Grupos ───────────────────────────────────────────────────────── */}
      <section class="space-y-2">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {t(msg`Groups`)}
          </h3>
          <Show when={writableChurches().length > 0}>
            <Button size="sm" data-testid="group-create" onClick={() => setGroupOpen(true)}>
              <Plus size={14} /> {t(msg`New group`)}
            </Button>
          </Show>
        </div>
        <Show
          when={(groups.data ?? []).length > 0}
          fallback={
            <p class="text-muted-foreground" data-testid="groups-empty">
              {t(msg`No groups synced to this account yet.`)}
            </p>
          }
        >
          <div class="space-y-2" data-testid="groups-list">
            <For each={groups.data ?? []}>
              {(g) => (
                <Link to="/groups/$groupId" params={{ groupId: g.id }} class="block">
                  <Card
                    data-testid="group-item"
                    data-group-id={g.id}
                    class="transition-colors hover:border-primary/40"
                  >
                    <CardContent class="flex items-center gap-3 py-3">
                      <span class="flex size-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
                        <Boxes size={16} />
                      </span>
                      <div class="min-w-0 flex-1">
                        <p class="truncate font-medium">{g.name}</p>
                        <p class="truncate text-sm text-muted-foreground">
                          {typeOf(g)?.name} · {categoryName(typeOf(g)?.category_id ?? null)} ·{" "}
                          {churchName(g.church_id)}
                        </p>
                      </div>
                      <div class="flex flex-wrap justify-end gap-1">
                        <For each={capsOfCategory(typeOf(g)?.category_id ?? null)}>
                          {(cap) => (
                            <span class="rounded-full bg-accent px-2 py-0.5 text-[10px] uppercase tracking-wide text-accent-foreground">
                              {cap}
                            </span>
                          )}
                        </For>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )}
            </For>
          </div>
        </Show>
      </section>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={typeOpen()}
        onOpenChange={setTypeOpen}
        title={t(msg`New group type`)}
        icon={<Tags size={20} />}
        submitTestId="group-type-submit"
        disabled={!typeName().trim() || !typeCategory()}
        onSubmit={createType}
      >
        <Field
          id="gt-name"
          label={t(msg`Your own name`)}
          value={typeName()}
          onInput={setTypeName}
          placeholder={t(msg`e.g. Worship Ministry, Life Group…`)}
          testId="group-type-name-input"
        />
        <Field id="gt-category" label={t(msg`System category`)}>
          <Select
            options={(categories.data ?? []).map((c) => ({
              value: c.id,
              label: `${c.name ?? c.id} (${capsOfCategory(c.id).join(", ")})`,
            }))}
            value={typeCategory()}
            onChange={setTypeCategory}
            aria-label={t(msg`System category`)}
          />
        </Field>
        <Show when={writableChurches().length > 1}>
          <Field id="gt-church" label={t(msg`Church`)}>
            <Select
              options={writableChurches().map((c) => ({ value: c.id, label: c.name ?? c.id }))}
              value={typeChurch() || writableChurches()[0]?.id}
              onChange={setTypeChurch}
              aria-label={t(msg`Church`)}
            />
          </Field>
        </Show>
      </FormDialog>

      <FormDialog
        open={groupOpen()}
        onOpenChange={setGroupOpen}
        title={t(msg`New group`)}
        icon={<Boxes size={20} />}
        submitTestId="group-submit"
        disabled={!groupName().trim() || !groupType()}
        onSubmit={createGroup}
      >
        <Field
          id="g-name"
          label={t(msg`Name`)}
          value={groupName()}
          onInput={setGroupName}
          testId="group-name-input"
        />
        <Field id="g-type" label={t(msg`Group type`)}>
          <Select
            options={(types.data ?? []).map((ty) => ({
              value: ty.id,
              label: `${ty.name ?? ty.id} · ${churchName(ty.church_id)}`,
            }))}
            value={groupType()}
            onChange={setGroupType}
            aria-label={t(msg`Group type`)}
            emptyAction={{
              label: t(msg`Create a group type first`),
              onSelect: () => {
                setGroupOpen(false);
                setTypeOpen(true);
              },
            }}
          />
        </Field>
      </FormDialog>
    </div>
  );
}
